/**
 * MerchantHome — the merchant "Resumen diario" standup, rebuilt to the approved
 * handoff design (docs/design/design_handoff_merchant/merchant-home, 2a/3a):
 * metrics header → worst-goal status banner → compact goal list → "Vendé más".
 *
 * Decisions applied (RESPUESTA-ENG.md): banner picks the WORST goal (severity
 * first, revenue-at-stake tiebreak); goal rows are non-interactive until the
 * detail screen exists; Revenue/OffStorefront cards are gone (header absorbs
 * the web-only figures); banner-test entry keeps the two-banner experiment
 * framing until bandits ship; test progress = time toward the 30-day cap.
 */

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text, Tag, ProgressBar } from '@nimbus-ds/components'
import { goalService, formatMetricValue, merchantTrend, merchantStatus, type Goal } from '@/api/goals'
import { storeService, type StoreLifecycle } from '@/api/store'
import { EstaSemanaSection, useQueue } from '@/components/merchant/EstaSemana'
import { funnelService } from '@/api/funnel'
import { trafficService, type TrafficCampaignRow } from '@/api/traffic'
import { CampaignMap, mapRows, isMapMeaningful } from '@/components/merchant/CampaignMap'
import { authService } from '@/api/auth'
import { experimentService } from '@/api/experiments'
import { isSelfServeBannerExperiment, timeProgressPct } from '@/pages/MerchantBannerResults'
import { isBannerSlot } from '@/pages/MerchantBannerOptimization'
import { trackAppEvent } from '@/lib/appTelemetry'
import { fmtInt, fmtMoney, fmtPct } from '@/lib/formatEsAr'

// Plain-language status line per goal status (merchant-facing, no jargon).
// `traffic` is the mix-router's acquisition disposition — distinct from a store problem.
const STATUS_LINE: Record<Goal['status'] | 'traffic', string> = {
  calibrating: 'Aprendiendo cómo funciona tu tienda. En unos días vas a ver resultados.',
  on_track: 'Vas bien.',
  watch: 'Para mirar de cerca.',
  at_risk: 'Necesita atención: revisá tu tienda (checkout, página).',
  traffic: 'Es el tráfico, no la tienda — revisá tus fuentes o hablá con quien maneja tus anuncios.',
  achieved: '¡Objetivo logrado!',
}

// Worst-first: store problems (at_risk) outrank a traffic shift; both outrank "watch".
const PRIORITY: Record<Goal['status'] | 'traffic', number> = {
  at_risk: 5, traffic: 4, watch: 3, on_track: 2, achieved: 1, calibrating: 0,
}

// Trend glyphs — hues fixed by the handoff (▲ #00935b · ▼ #b91c1c · ▪ #b0b0b0).
const TREND_STYLE = {
  up: { cls: 'text-[#00935b]', glyph: '▲' },
  down: { cls: 'text-[#b91c1c]', glyph: '▼' },
  flat: { cls: 'text-[#b0b0b0]', glyph: '▪' },
} as const

interface HeaderMetrics {
  revenue: number
  sessions: number
  conversions: number
}

// Web conversions: prefer the explicit total, else the conversion step (same
// fallback RevenueAnalyticsCard used before the header absorbed it).
function webConversions(a: any): number {
  if (typeof a?.total_conversions === 'number') return a.total_conversions
  const steps = a?.funnel_steps || []
  const conv = steps.find((s: any) => s?.is_conversion_step)
  return conv?.total_sessions || 0
}

type BannerTestCard =
  | { kind: 'running'; sessions: number; progressPct: number }
  | { kind: 'link' }

/**
 * The optimization slot's Home card (OPTIMIZATIONS-SPEC.md §5). It COEXISTS with the A/B card
 * rather than replacing it: the two families answer different questions, and a merchant who has
 * one running should still be able to see the other offered.
 */
type BannerSlotCard =
  | { kind: 'running'; leaderShare: number; leaderLabel: string; learning: boolean; fatigued: boolean }
  | { kind: 'link' }

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function MetricsHeader({ m }: { m: HeaderMetrics }) {
  const conversionRate = m.sessions > 0 ? fmtPct(m.conversions / m.sessions) : '—'
  const avgTicket = m.conversions > 0 ? fmtMoney(m.revenue / m.conversions) : '—'
  return (
    <div className="flex flex-col gap-1.5 px-0.5 md:flex-row md:flex-wrap md:items-end md:justify-between md:gap-6">
      <div className="flex flex-col gap-1.5">
        <Text fontSize="caption">Tus últimos 7 días</Text>
        <div className="text-[2rem] font-bold leading-[1.1] tracking-[-0.01em] tabular-nums text-[var(--nimbus-colors-neutral-textHigh)] md:text-[2.5rem]">
          {fmtMoney(m.revenue)}
        </div>
        <Text as="span" className="tabular-nums">
          {fmtInt(m.conversions)} {m.conversions === 1 ? 'venta' : 'ventas'} · {fmtInt(m.sessions)} sesiones
        </Text>
        {/* Mobile: inline metric pair under the revenue block. */}
        <div className="flex gap-4 text-sm md:hidden">
          <span className="text-[var(--nimbus-colors-neutral-textLow)]">
            Conversión <span className="font-semibold tabular-nums text-[var(--nimbus-colors-neutral-textHigh)]">{conversionRate}</span>
          </span>
          <span className="text-[var(--nimbus-colors-neutral-textLow)]">
            Ticket promedio <span className="font-semibold tabular-nums text-[var(--nimbus-colors-neutral-textHigh)]">{avgTicket}</span>
          </span>
        </div>
      </div>
      {/* Desktop: metric columns bottom-aligned against the big number. */}
      <div className="hidden gap-8 pb-1 md:flex">
        {[
          ['Conversión', conversionRate],
          ['Ticket promedio', avgTicket],
        ].map(([label, value]) => (
          <div key={label} className="flex flex-col gap-0.5 text-right">
            <Text as="span" fontSize="caption">{label}</Text>
            <span className="text-xl font-bold tabular-nums text-[var(--nimbus-colors-neutral-textHigh)]">{value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function StatusBanner({ emoji, title, sub }: { emoji: string; title: string; sub: string }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-[color-mix(in_srgb,var(--nimbus-colors-primary-interactive)_25%,var(--nimbus-colors-primary-surface))] bg-[var(--nimbus-colors-primary-surface)] px-4 py-3">
      <span className="text-xl leading-none">{emoji}</span>
      <p className="m-0 text-sm leading-snug">
        <span className="font-medium text-[var(--nimbus-colors-neutral-textHigh)]">{title}</span>{' '}
        <span className="text-[var(--nimbus-colors-neutral-textLow)]">{sub}</span>
      </p>
    </div>
  )
}

/**
 * Phase-0 push (roadmap R1, U1): the store is connected but the platform cannot see it
 * yet. Never an empty screen — one sentence naming what is missing, from the health the
 * hourly pass wrote. Replaces the standup until the health check passes.
 */
function Phase0Screen({ lc }: { lc: StoreLifecycle }) {
  const { tracking, orders } = lc.health
  let title: string, sub: string
  // Impersonal voice (user-set): the screen states what is registered, never "vemos".
  if (tracking === 'missing') {
    title = 'Todavía no hay visitas registradas en tu tienda.'
    sub = 'Revisá que el script de TN-Xperiment esté activo. Apenas lleguen las primeras visitas, esta pantalla cambia sola.'
  } else if (tracking === 'stale') {
    const days = lc.evidence.last_session_at
      ? Math.max(1, Math.round((Date.now() - new Date(lc.evidence.last_session_at).getTime()) / 86400000))
      : null
    title = days ? `No hay visitas registradas desde hace ${days} día${days === 1 ? '' : 's'}.` : 'No hay visitas registradas.'
    sub = 'Si cambiaste el diseño de tu tienda, es probable que el script se haya desactivado. Revisalo y la medición se reanuda sola.'
  } else if (orders === 'missing_webhook') {
    title = 'Hay visitas registradas, pero los pedidos no están llegando.'
    sub = 'La conexión de pedidos no está activa. Reconectá la app desde tu administrador de Tiendanube.'
  } else {
    title = 'Hay visitas registradas. Falta el primer pedido.'
    sub = 'Cuando llegue, empieza la lectura de tu tienda con tus números.'
  }
  return (
    <Card>
      <div className="flex flex-col gap-2 py-2">
        <Text fontSize="highlight" fontWeight="medium">{title}</Text>
        <Text color="neutral-textLow">{sub}</Text>
      </div>
    </Card>
  )
}

/**
 * The Lectura entry (R1 U3): shown once the store baseline set holds session rates
 * (`lifecycle.lectura` preliminar | locked). One line the merchant can read at a glance —
 * how many of every 100 visits leave without looking — and the link to the full read.
 */
function LecturaSection({ lc }: { lc: StoreLifecycle | null }) {
  if (!lc || lc.lectura === 'none') return null
  const locked = lc.lectura === 'locked'
  return (
    <section className="flex flex-col gap-2">
      <Text fontWeight="medium">{locked ? 'La lectura de tu tienda' : 'Tu primera lectura'}</Text>
      <Card>
        <div className="flex flex-col gap-2">
          <Text>
            {locked
              ? 'Quiénes llegan, qué hacen y cuántos compran. Es el punto de partida contra el que se compara cada cambio.'
              : 'Quiénes llegan y qué hacen en tu tienda. Cuántos compran se agrega solo cuando haya suficientes ventas.'}
          </Text>
          <Link to="/merchant/lectura" className="text-sm text-[var(--nimbus-colors-primary-textLow)] underline-offset-2 hover:underline">
            Ver la lectura
          </Link>
        </div>
      </Card>
    </section>
  )
}

function GoalRow({ goal, last }: { goal: Goal; last: boolean }) {
  const m = merchantStatus(goal)
  const isCalibrating = m.status === 'calibrating'
  const trend = merchantTrend(goal.primary_metric.current)
  const statusText = m.note || STATUS_LINE[m.status]
  return (
    <div
      className={`flex items-center gap-2.5 py-3 md:gap-3 md:py-3.5 ${last ? '' : 'border-b border-[#f0f0f0]'}`}
    >
      <span className="text-base leading-none" aria-hidden="true">{m.light}</span>
      <span className="min-w-0 flex-1 truncate text-sm text-[var(--nimbus-colors-neutral-textHigh)]">
        {goal.name}
      </span>
      {/* Status text rides only on desktop (mobile omits it for space); it is
          ALSO the screen-reader status since the emoji is decorative. */}
      <span className="mr-2 hidden max-w-[45%] truncate text-xs text-[var(--nimbus-colors-neutral-textLow)] md:block">
        {statusText}
      </span>
      <span className="sr-only">{statusText}</span>
      {!isCalibrating && (
        <>
          <span className="text-sm font-semibold tabular-nums text-[var(--nimbus-colors-neutral-textHigh)]">
            {formatMetricValue(goal.primary_metric.binding.metric, goal.primary_metric.current?.value)}
          </span>
          {trend && (
            <span className={`text-xs ${TREND_STYLE[trend.tone].cls}`} title={trend.label}>
              {TREND_STYLE[trend.tone].glyph}
              <span className="sr-only">{trend.label}</span>
            </span>
          )}
        </>
      )}
    </div>
  )
}

/**
 * "Tu tráfico" entry — the chart itself, not a text teaser.
 *
 * The standup's traffic status line already tells merchants "es el tráfico, no la tienda —
 * revisá tus fuentes"; this is where that sentence finally leads. Carrying the real map
 * (rather than a link with a promise) means the card answers the question at a glance and
 * the click is for the detail, which is how every other card on this page behaves.
 *
 * Self-hiding: no campaigns measured yet → no card. Home never shows an empty frame.
 */
function TrafficSection({ rows }: { rows: TrafficCampaignRow[] }) {
  if (!isMapMeaningful(rows)) return null
  return (
    <section className="flex flex-col gap-2">
      <Text fontWeight="medium">Tu tráfico</Text>
      <Link to="/merchant/trafico" className="block no-underline">
        <Card>
          <Card.Body>
            <div className="flex flex-col gap-2">
              {/* Wider aspect than the detail view's: this column is ~700px, and a tall
                  plot would push "Vendé más" off the fold on the standup. */}
              <CampaignMap rows={rows} width={440} height={210} />
              <Text fontSize="caption">Mirá qué campañas te sirven →</Text>
            </div>
          </Card.Body>
        </Card>
      </Link>
    </section>
  )
}

function SellMoreSection({
  bannerCard,
  slotCard,
}: {
  bannerCard: BannerTestCard | null
  slotCard: BannerSlotCard | null
}) {
  return (
    <section className="flex flex-col gap-2">
      <Text fontWeight="medium">Vendé más</Text>
      <div className="flex flex-col gap-3">
        {bannerCard?.kind === 'running' && (
          <Link to="/merchant/banner-test/results" className="block no-underline">
            <Card>
              <Card.Body>
                <div className="flex flex-col gap-2.5">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl leading-none">🧪</span>
                    <div className="min-w-0">
                      <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
                        Tu prueba de banner
                      </Text>
                      <Text fontSize="caption">
                        {bannerCard.sessions > 0
                          ? `Recolectando datos — ya participaron ${fmtInt(bannerCard.sessions)} visitas.`
                          : 'La prueba está en marcha. En cuanto tus visitas empiecen a participar, vas a ver el avance acá.'}
                      </Text>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <ProgressBar value={bannerCard.progressPct} appearance="primary" />
                    <Text fontSize="caption">Te avisamos cuando haya una respuesta confiable.</Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Link>
        )}

        {bannerCard?.kind === 'link' && (
          <Link to="/merchant/banner-test/results" className="block no-underline">
            <Card>
              <Card.Body>
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">🧪</span>
                  <div className="min-w-0">
                    <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
                      Tu prueba de banner
                    </Text>
                    <Text fontSize="caption">Mirá cómo viene y si ya hay un resultado.</Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Link>
        )}

        {/* Entry to a NEW test — hidden while one is running (a second concurrent
            test would fight over the same banner element). */}
        {bannerCard?.kind !== 'running' && (
          <Link to="/merchant/banner-test" className="block no-underline">
            <Card>
              <Card.Body>
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">🖼️</span>
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
                        Probá otro banner
                      </Text>
                      <div className="shrink-0 whitespace-nowrap">
                        <Tag appearance="primary">Nuevo</Tag>
                      </div>
                    </div>
                    <Text fontSize="caption">
                      Subí un banner nuevo y tu tienda lo compara con el actual: la mitad de tus
                      visitas ve cada uno, y medís cuál genera más clics.
                    </Text>
                    <Text fontSize="caption">Sin configuración · lo frenás cuando quieras.</Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Link>
        )}

        {slotCard?.kind === 'running' && (
          <Link to="/merchant/banner-optimization/results" className="block no-underline">
            <Card>
              <Card.Body>
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">{slotCard.fatigued ? '📉' : '🎛️'}</span>
                  <div className="min-w-0">
                    <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
                      Tu banner optimizado
                    </Text>
                    <Text fontSize="caption">
                      {slotCard.fatigued
                        ? 'Tu banner está perdiendo interés — mirá qué conviene hacer.'
                        : slotCard.learning
                          ? 'Todavía no hay datos para saber cuál funciona mejor. Por ahora se muestran por igual.'
                          : `Hoy mostramos más seguido ${slotCard.leaderLabel} (${slotCard.leaderShare}% de las visitas).`}
                    </Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Link>
        )}

        {slotCard?.kind === 'link' && (
          <Link to="/merchant/banner-optimization/results" className="block no-underline">
            <Card>
              <Card.Body>
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">🎛️</span>
                  <div className="min-w-0">
                    <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
                      Tu banner optimizado
                    </Text>
                    <Text fontSize="caption">Mirá cómo viene la rotación.</Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Link>
        )}

        {/* Entry to a NEW slot — hidden while one is running, same reason as the A/B entry: two
            of them would fight over the same banner element. */}
        {slotCard?.kind !== 'running' && (
          <Link to="/merchant/banner-optimization" className="block no-underline">
            <Card>
              <Card.Body>
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">🎛️</span>
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
                        Optimizá tu banner
                      </Text>
                      <div className="shrink-0 whitespace-nowrap">
                        <Tag appearance="primary">Nuevo</Tag>
                      </div>
                    </div>
                    <Text fontSize="caption">
                      Subí 2 o 3 banners además del tuyo y mostramos más seguido el que mejor
                      funciona. No termina: se ajusta solo.
                    </Text>
                    <Text fontSize="caption">
                      Sin configuración · te avisamos si el ganador se gasta.
                    </Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Link>
        )}

        <Card>
          <Card.Body>
            <div className="flex items-start gap-3 opacity-75">
              <span className="text-2xl leading-none">📣</span>
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Text fontWeight="medium" color="neutral-textHigh" className="leading-tight">
                    Probá otro título en tu página
                  </Text>
                  <div className="shrink-0 whitespace-nowrap">
                    <Tag appearance="neutral">Próximamente</Tag>
                  </div>
                </div>
                <Text fontSize="caption">Compará dos textos de portada sin tocar tu diseño.</Text>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MerchantHome() {
  const [goals, setGoals] = useState<Goal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [metrics, setMetrics] = useState<HeaderMetrics | null>(null)
  const [bannerCard, setBannerCard] = useState<BannerTestCard | null>(null)
  const [slotCard, setSlotCard] = useState<BannerSlotCard | null>(null)
  const [trafficRows, setTrafficRows] = useState<TrafficCampaignRow[]>([])
  const [lifecycle, setLifecycle] = useState<StoreLifecycle | null>(null)
  // R2: the card queue (hygiene ficha + plan cards), one rule shared with the weekly brief.
  const [queue, setQueue] = useQueue(authService.getCurrentStoreId())

  // Lifecycle (R1 P2): decides the whole screen. Phase 0 replaces the standup with the
  // push; phases 2+ render the standup with the hygiene ficha. Failure-silent: with no
  // lifecycle (never aggregated) the standup renders as before.
  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    storeService.getStore(storeId)
      .then((info) => {
        setLifecycle(info?.lifecycle || null)
        if (info?.lifecycle?.phase === 'conectar') trackAppEvent('phase0_viewed', { tracking: info.lifecycle.health.tracking, orders: info.lifecycle.health.orders })
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    goalService.listGoals(7)
      .then((res) => {
        // The system hygiene goal is a queue, not an objective: it never lists under
        // "Tus objetivos". Its served ficha renders in "Esta semana" through the queue (U5, R2).
        setGoals(res.goals.filter((g) => g.kind !== 'hygiene'))
      })
      .catch((e) => setError(e?.message || 'No se pudieron cargar tus objetivos.'))
      .finally(() => setLoading(false))
  }, [])

  // Header metrics — web-only figures (POS/manual/marketplace excluded upstream).
  // Non-blocking: the header self-hides if analytics aren't available.
  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    funnelService.getStoreFunnelAnalytics(storeId, 7)
      .then((res: any) => {
        const a = res?.tnx_analytics
        if (!a) return
        setMetrics({
          revenue: a.product_analytics?.product_performance?.total_product_revenue || 0,
          sessions: a.unique_sessions || 0,
          conversions: webConversions(a),
        })
      })
      .catch(() => {})
  }, [])

  // Traffic map for the "Tu tráfico" entry. Non-blocking and failure-silent, like the
  // header: this card is a bonus on the standup, never a reason for it not to render.
  useEffect(() => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return
    trafficService
      .getTrafficValue(storeId, 7)
      .then((res) => setTrafficRows(mapRows(res.channels)))
      .catch(() => {})
  }, [])

  // Banner-test card state (non-blocking): a running test shows collecting
  // progress; any past test shows the results link; none shows the proposal
  // entry (and fires the pilot-funnel proposal_viewed event, B10).
  useEffect(() => {
    experimentService
      .getExperiments()
      .then(async (all) => {
        // --- A/B card ---
        const tests = all
          .filter(isSelfServeBannerExperiment)
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
        if (tests.length === 0) {
          trackAppEvent('proposal_viewed')
        } else {
          const active = tests.find((e) => e.status === 'active')
          if (!active) {
            setBannerCard({ kind: 'link' })
          } else {
            const results = await experimentService.getExperimentResults(active.id).catch(() => null)
            const s = (results as any)?.sessions || {}
            setBannerCard({
              kind: 'running',
              sessions: (s.control || 0) + (s.treatment || 0),
              progressPct: timeProgressPct(active),
            })
          }
        }

        // --- Optimization slot card ---
        // A running slot summarises what it is doing TODAY (that is the whole product promise); a
        // fatigue flag takes over the line, because it is the one thing that needs the merchant.
        const slots = all
          .filter(isBannerSlot)
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
        if (slots.length === 0) return
        const liveSlot = slots.find((e) => e.status === 'active')
        if (!liveSlot) {
          setSlotCard({ kind: 'link' })
          return
        }
        const alloc = liveSlot.allocation
        const leader = alloc?.leader || liveSlot.optimization?.pinned_arm || null
        const challengers = Object.keys(liveSlot.variants || {})
          .filter((k) => k !== 'control')
          .sort()
        setSlotCard({
          kind: 'running',
          learning: !alloc || alloc.reason === 'learning' || alloc.reason === 'no_data',
          fatigued: liveSlot.fatigue?.status === 'flagged',
          leaderLabel:
            !leader || leader === 'control'
              ? 'tu banner de siempre'
              : `el banner ${challengers.indexOf(leader) + 2}`,
          leaderShare: leader ? Number(liveSlot.traffic_split?.[leader]) || 0 : 0,
        })
      })
      .catch(() => trackAppEvent('proposal_viewed'))
  }, [])

  if (loading) return <Text className="py-6 text-center">Cargando…</Text>

  // Phase 0 (R1 U1): connected but unseen. The push replaces the standup entirely —
  // every other section would be empty or misleading without tracking or orders.
  if (lifecycle?.phase === 'conectar') {
    return (
      <div className="flex flex-col gap-5 md:gap-6">
        <Phase0Screen lc={lifecycle} />
      </div>
    )
  }

  // Goals are ONE section of the standup, not its gate: a store with no goals
  // configured (or a failed goals load) still gets the metrics header, the
  // traffic map and "Vendé más" — those loads are independent on purpose.
  // Worst-first (RESPUESTA §5): severity ranks, revenue-at-stake breaks ties.
  // Segmented goals are a CONSULTANT instrument (CR1 v1) — never surfaced here.
  const view = goals
    .filter((g) => !g.primary_metric?.segment)
    .map((g) => ({ goal: g, m: merchantStatus(g), stake: g.impact?.revenue_at_stake || 0 }))
  const sorted = [...view].sort(
    (a, b) => (PRIORITY[b.m.status] - PRIORITY[a.m.status]) || (b.stake - a.stake)
  )
  const allCalibrating = view.every((v) => v.m.status === 'calibrating')
  const worst = sorted[0]

  let banner: { emoji: string; title: string; sub: string } | null = null
  const calibrandoPhase = lifecycle?.phase === 'calibrando'
  if (calibrandoPhase) {
    // Phase 2 (R1 U1): the store-level calibration is the message, whatever the goals say.
    banner = {
      emoji: '⏳',
      title: 'Tu tienda está en período de lectura.',
      sub: queue && queue.shown.length > 0 ? 'Mientras tanto, esta semana hay algo para hacer.' : 'En unos días aparece la primera lectura.',
    }
  } else if (view.length === 0) {
    banner = null // nothing goal-derived to summarise
  } else if (allCalibrating) {
    banner = { emoji: '⏳', title: 'Tus objetivos están en período de lectura.', sub: 'En unos días vas a empezar a ver resultados.' }
  } else if (worst && PRIORITY[worst.m.status] >= PRIORITY.watch) {
    banner = { emoji: worst.m.light, title: worst.goal.name, sub: worst.m.note || STATUS_LINE[worst.m.status] }
  } else {
    banner = { emoji: '🟢', title: 'Todo va bien.', sub: 'Tus objetivos están en camino.' }
  }

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      {metrics && <MetricsHeader m={metrics} />}

      {banner && <StatusBanner {...banner} />}

      <div className="flex flex-col gap-5 md:grid md:grid-cols-[1fr_380px] md:items-start md:gap-6">
        {/* The wide column carries the data (goals, then the traffic map). "Vendé más" is a
            column of offers and stays narrow — putting the chart there squeezed it into a
            380px sliver while the goals column ran out of content halfway down the page. */}
        <div className="flex flex-col gap-5 md:gap-6">
  
          <EstaSemanaSection queue={queue} onQueue={setQueue} />

          <LecturaSection lc={lifecycle} />

          <section className="flex flex-col gap-2">
            <Text fontWeight="medium">Tus objetivos</Text>
            <Card padding="none">
              <div className="px-4 md:px-5">
                {sorted.length > 0 ? (
                  sorted.map((v, i) => (
                    <GoalRow key={v.goal.goal_id} goal={v.goal} last={i === sorted.length - 1} />
                  ))
                ) : (
                  <div className="py-4">
                    <Text fontSize="caption">
                      {error
                        ? 'No se pudieron cargar tus objetivos. Probá de nuevo en un rato.'
                        : 'Todavía no hay objetivos definidos para tu tienda. Tu consultor los va a configurar pronto.'}
                    </Text>
                  </div>
                )}
              </div>
            </Card>
          </section>

          <TrafficSection rows={trafficRows} />
        </div>

        <SellMoreSection bannerCard={bannerCard} slotCard={slotCard} />
      </div>
    </div>
  )
}
